import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Tooltip } from '@mui/material';
import { Scan } from '../common/model/dto/scan';
import { Resource } from '../common/model/type/resource.enum';
import { RecordColumn } from '../common/model/ui/record-list.type';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvScanCreatedBy from '../components/UI/molecules/OvScanCreatedBy';
import OvScanResultSummary from '../components/UI/molecules/OvScanResultSummary';
import Colours from '../design-system/colours';
import useAuthorized from './use-authorized';

export function useScanListColumns(
  onDelete: (scanId: string) => void
): RecordColumn<Scan>[] {
  const { t } = useTranslation();
  const applicationOperations = useAuthorized(Resource.Application);
  const scanOperations = useAuthorized(Resource.Scan);
  const userOperations = useAuthorized(Resource.User);
  const scanReadAllowed =
    scanOperations.read || applicationOperations.supervise;
  const scanDeleteAllowed =
    scanOperations.delete || applicationOperations.supervise;
  const userReadAllowed = !!(
    userOperations.read || applicationOperations.supervise
  );

  const columns: RecordColumn<Scan>[] = [
    {
      id: 'scan',
      header: t('common.scan'),
      renderCell: (scan) => (
        <Thumbnail alt={t('scanList.imageAlt')} src={scan.output_image_url} />
      ),
    },
    {
      id: 'created-by',
      header: t('common.createdBy'),
      cardRole: 'heading',
      renderCell: (scan) => (
        <OvScanCreatedBy scan={scan} isUserReadAllowed={userReadAllowed} />
      ),
    },
    {
      id: 'results',
      header: t('common.dailyData.results'),
      renderCell: (scan) => <OvScanResultSummary scan={scan} />,
    },
    {
      id: 'toolkit',
      header: t('common.dailyData.toolkit'),
      renderCell: (scan) => scan.toolkit_output?.version ?? '-',
    },
  ];

  if (scanDeleteAllowed) {
    columns.push({
      id: 'operation',
      header: t('common.operation'),
      isHeaderHidden: true,
      renderCell: (scan) => (
        <Tooltip title={t('common.dailyData.deleteScan')}>
          <span>
            <DeleteButton
              aria-label={t('common.dailyData.deleteScan')}
              disabled={scan.is_daily_log_record}
              onClick={() => scan.id && onDelete(scan.id)}
            >
              <DeleteIcon />
            </DeleteButton>
          </span>
        </Tooltip>
      ),
    });
  }

  if (scanReadAllowed) {
    columns.push({
      id: 'action',
      header: t('common.action'),
      isHeaderHidden: true,
      cardRole: 'footer',
      renderCell: (_, detailsPath = '') => (
        <OvPillLink to={detailsPath}>{t('common.actions.details')}</OvPillLink>
      ),
    });
  }

  return columns;
}

const Thumbnail = styled.img`
  display: block;
  max-width: 7rem;
  max-height: 5rem;
  object-fit: contain;
`;

const DeleteButton = styled(IconButton)`
  && {
    width: 2rem;
    height: 2rem;
    padding: 0.375rem;
    color: ${Colours.OV_BASE};
  }
`;
